// src/components/dashboard/wall/PreviousChallangeLeaderBoardTableComponent.jsx
import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT } from "../../lib/api-url";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";

const pointsForRank = (rank) => Math.max(0, 45 - Number(rank) * 5);

function safeNum(n, fallback = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function shapeRow(it) {
  const rank =
    safeNum(it?.rank_in_stage) || safeNum(it?.level_global_rank) || 0;
  const loginNum = safeNum(it?.login);
  const username = String(it?.username ?? it?.login ?? "");
  const equity = safeNum(it?.equity ?? it?.score, 0);
  const balance = safeNum(it?.balance, equity);
  return {
    id: loginNum || username,
    login: loginNum,
    username,
    equity,
    balance,
    points: pointsForRank(rank),
    rank,
    profileImage: it?.profileImage || DEFAULT_AVATAR,
  };
}

const itemVariantsSmooth = {
  hidden: { opacity: 0, x: 56, filter: "blur(4px)" },
  show: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { type: "tween", duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

function PreviousChallangeLeaderBoardTableComponent({
  onDataReady,
  play = true,
}) {
  const [items, setItems] = useState([]);
  const notifiedRef = useRef(false);
  const reduceMotion = useReducedMotion();

  const itemVariants = reduceMotion
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.2 } },
      }
    : itemVariantsSmooth;

  const notifyReadyOnce = () => {
    if (!notifiedRef.current) {
      notifiedRef.current = true;
      onDataReady?.();
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.users.getLeaderBoardData, {
          signal: controller.signal,
        });
        const arr = res?.data?.next_level?.items ?? [];
        setItems(Array.isArray(arr) ? arr : []);
        notifyReadyOnce();
      } catch (e) {
        if (e.name !== "CanceledError" && e.message !== "canceled") {
          console.error("Leaderboard API error:", e);
          notifyReadyOnce();
        }
      }
    })();
    return () => controller.abort();
  }, []);

  const rows = useMemo(() => {
    const shaped = (Array.isArray(items) ? items : []).map(shapeRow);
    shaped.sort((a, b) => {
      const ar = a.rank || Number.MAX_SAFE_INTEGER;
      const br = b.rank || Number.MAX_SAFE_INTEGER;
      return ar - br;
    });
    return shaped;
  }, [items]);

  const rightCol = rows.slice(0, 10);
  const leftCol = rows.slice(10, 20);

  const fmt = (v) =>
    v == null || Number.isNaN(Number(v))
      ? "-"
      : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const Header = () => (
    <div  className="header-component w-[400px] relative">
      <img
        src="/red-table/red-table-header.svg"
        className="w-[240px] z-[2] absolute top-[-20px] left-[100px]"
        alt=""
      />
      <div className="z-[4] right-[22%] top-[-16px] header-text-container flex items-center justify-center absolute gap-[9px]">
        <span className="text-[10px] text-[#FFFFFF] font-semibold">امتیاز</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">
          موجودی‌لحظه‌ای
        </span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">موجودی</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">رتبه</span>
      </div>
    </div>
  );

  const Card = ({ user }) => (
    <motion.div
      className="card-container relative w-[400px] z-[3]"
      variants={itemVariants}
      layout
    >
      <img src="/red-table/red-table-banner.svg" className="w-[450px]" alt="" />
      <img
        src="/red-table/red-table-sub-banner.svg"
        className="absolute right-[64px] bottom-[-8px] w-[137px]"
        alt=""
      />
      <img
        src="/red-table/red-table-user-box.svg"
        className="absolute right-0 top-[-4px] w-[83px]"
        alt=""
      />
      <img
        src={user.profileImage || DEFAULT_AVATAR}
        className="w-[54px] absolute right-[16px] top-[-32px]"
        alt={user.username}
        loading="lazy"
      />
      <div className="text-container flex items-center justify-center gap-3 absolute right-[90px] top-[10px]">
        <div className="points-container flex justify-center items-center gap-[24px] ">
          <span className="text-[#FFFFFF] text-[14px] font-bold">
            {fmt(user.points)}
          </span>
          <span className="text-[#FFFFFF] text-[14px] font-bold">
            {fmt(user.equity)}
          </span>
          <span className="text-[#FFFFFF] text-[14px] font-bold">
            {fmt(user.balance)}
          </span>
          <span className="text-[#FFFFFF] text-[14px] font-bold">
            {user.rank}
          </span>
        </div>
        <span className="text-[#FFFFFF] text-[10px] font-bold w-[80px] text-right ">
          {user.username}
        </span>
      </div>
    </motion.div>
  );

  return (
    <div className="flex justify-center gap-[182px] mb-[32px] flex-row-reverse">
      <div>
        <Header />
        <motion.div
          className="w-full flex flex-col items-center justify-center gap-[38px]"
          initial="hidden"
          animate={play ? "show" : "hidden"}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: 0.18, delayChildren: 0.18 },
            },
          }}
        >
          {rightCol.map((user) => (
            <Card key={user.id} user={user} />
          ))}
        </motion.div>
      </div>
      <div>
        <Header />
        <motion.div
          className="w-full flex flex-col items-center justify-center gap-[38px]"
          initial="hidden"
          animate={play ? "show" : "hidden"}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: 0.18, delayChildren: 0.48 },
            },
          }}
        >
          {leftCol.map((user) => (
            <Card key={user.id} user={user} />
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default memo(PreviousChallangeLeaderBoardTableComponent);
